/**
 * Marketplace Comparison Matrix V2 — Feb 2026
 *
 * Powers 8 head-to-head decision-intent comparison pages ranking for
 * "X vs Y" commercial queries. All render through
 * /pages/MarketplaceComparisonPageV2.jsx from this single config file.
 *
 * Architecture:
 *   MARKETPLACE_ATTRS   – deep per-marketplace attributes (reused across pairs)
 *   COMPARISONS         – pair-specific narrative (winner, direct answer, FAQ, prosCons)
 *   getComparisonBySlug – lookup helper
 *   getComparisonsByMarketplace – used for internal linking across hub pages
 *
 * Scale-ready: to add 30-50 more comparisons, only extend COMPARISONS —
 * no template or route file changes required (routes are auto-derived).
 */
import { MARKETPLACES } from './marketplaceDataSource';

// ── Deep per-marketplace attribute table ──────────────────────────────
// Each marketplace has a rich record used by the comparison table rows.
// Missing marketplaces (amazon, tataCliq) filled inline where SSOT lacks them.
export const MARKETPLACE_ATTRS = {
  myntra: {
    name: 'Myntra',
    slug: 'myntra',
    logo: 'MY',
    accent: 'from-pink-500 to-rose-500',
    timeline: MARKETPLACES.myntra.timeline,
    approvalDifficulty: MARKETPLACES.myntra.approvalDifficulty,
    commission: '18-32%',
    gmv: '₹35,000 Cr',
    monthlyActiveUsers: '70M+',
    aovBand: MARKETPLACES.myntra.aovBand,
    categoryFocus: MARKETPLACES.myntra.categoryFocus,
    sellerRequirements: 'GST, brand trademark (Class 25), 20+ hero SKUs, Myntra-spec photography, agency filing recommended',
    logistics: 'Myntra Fulfilment (MF/MJIT) + Self-ship, 7-day fulfilment SLA',
    returns: '30-day open-box return window, RTO 22-28%',
    settlementCycle: 'T+7 to T+15 days',
    marketing: 'EORS, End of Reason Sale, Fashion Show, editorial curation',
    advertising: 'PLA (Product Listing Ads), Sponsored Brand, Display, Video',
    catalogQuality: 'High (Myntra-spec studio shoots mandatory)',
    brandSuit: 'Fashion, apparel, footwear, accessories, D2C fashion',
    growthPotential: MARKETPLACES.myntra.growthPotential,
    amComplexity: MARKETPLACES.myntra.amComplexity,
    hubHref: '/myntra-account-management',
    approvalHref: '/myntra-brand-approval-india',
    pros: [
      '70M+ MAU with fashion-first shopper intent',
      'Highest fashion GMV (₹35,000 Cr) in India',
      'Editorial curation + Myntra Studio content velocity',
      'PLA + Sponsored Brand ad tools drive predictable CAC',
    ],
    cons: [
      'Studio-shoot cost is 30-40% higher than Amazon',
      'RTO 22-28% needs active address-scrub + COD blocking',
      'Commission 18-32% higher than Amazon fashion',
    ],
  },
  nykaa: {
    name: 'Nykaa',
    slug: 'nykaa',
    logo: 'NY',
    accent: 'from-fuchsia-500 to-pink-500',
    timeline: MARKETPLACES.nykaa.timeline,
    approvalDifficulty: MARKETPLACES.nykaa.approvalDifficulty,
    commission: '25-35%',
    gmv: '₹6,500 Cr',
    monthlyActiveUsers: '30M+',
    aovBand: MARKETPLACES.nykaa.aovBand,
    categoryFocus: MARKETPLACES.nykaa.categoryFocus,
    sellerRequirements: 'GST, FSSAI (nutraceutical), CDSCO (cosmetics), ingredient story, D2C traction proof, content-led brand book',
    logistics: 'Nykaa Fulfilment (NF) + Seller-ship, 5-day pick-up SLA',
    returns: '14-day return window, RTO 8-12% (lowest in industry)',
    settlementCycle: 'T+15 days',
    marketing: 'Pink Pass, Pink Friday Sale, editorial + Nykaa TV, Nykaa Beauty Book',
    advertising: 'Sponsored Products, Brand Store, Search + Category ads, Video',
    catalogQuality: 'Very high (buyer team gated)',
    brandSuit: 'Beauty, cosmetics, personal care, wellness, fragrance',
    growthPotential: MARKETPLACES.nykaa.growthPotential,
    amComplexity: MARKETPLACES.nykaa.amComplexity,
    hubHref: '/nykaa-account-management',
    approvalHref: '/nykaa-brand-approval',
    pros: [
      'India\'s dedicated beauty leader with 28% repeat-purchase',
      'Curated buyer team ensures premium shelf placement',
      'Lowest RTO in industry (8-12%)',
      'Pink Pass discount funding + editorial velocity',
    ],
    cons: [
      'Buyer-team gated approval (20-25 days)',
      'Content refresh required every 6-8 weeks',
      'Commission 25-35% is the highest bracket in beauty',
    ],
  },
  ajio: {
    name: 'AJIO',
    slug: 'ajio',
    logo: 'AJ',
    accent: 'from-amber-500 to-orange-500',
    timeline: MARKETPLACES.ajio.timeline,
    approvalDifficulty: MARKETPLACES.ajio.approvalDifficulty,
    commission: '18-28%',
    gmv: '₹14,500 Cr',
    monthlyActiveUsers: '40M+',
    aovBand: MARKETPLACES.ajio.aovBand,
    categoryFocus: MARKETPLACES.ajio.categoryFocus,
    sellerRequirements: 'GST, brand trademark, track-appropriate AOV (₹399+ Brand Mall / ₹3,500+ Luxe / ₹1,200+ Indie)',
    logistics: 'AJIO Fulfilment (AJF) + Self-ship, Reliance last-mile network',
    returns: '30-day return window, RTO 18-24%',
    settlementCycle: 'T+15 to T+30 days',
    marketing: 'AJIO Big Bold Sale, Luxe Sale, Reliance ecosystem cross-promo (JioMart, Reliance Trends)',
    advertising: 'Sponsored Products, Brand Ads, Ajio Ads (self-serve beta)',
    catalogQuality: 'High (3-track structure enforces AOV compliance)',
    brandSuit: 'Emerging D2C fashion, tier-2/3 mainstream, AJIO Luxe designers',
    growthPotential: MARKETPLACES.ajio.growthPotential,
    amComplexity: MARKETPLACES.ajio.amComplexity,
    hubHref: '/ajio-account-management',
    approvalHref: '/ajio-seller-registration',
    pros: [
      '3-track structure (Brand Mall/Luxe/Indie) fits all AOV bands',
      '35-45% YoY growth — fastest fashion marketplace',
      'Reliance ecosystem cross-promo (JioMart, Trends stores)',
      'Startup-friendlier than Myntra for emerging brands',
    ],
    cons: [
      'Settlement can stretch to T+30 for some tracks',
      'Ajio Ads still in self-serve beta',
      'Less editorial content velocity vs Myntra',
    ],
  },
  tataCliq: {
    name: 'Tata CLiQ',
    slug: 'tata-cliq',
    logo: 'TC',
    accent: 'from-blue-600 to-indigo-600',
    timeline: '25-40 days',
    approvalDifficulty: 'High',
    commission: '15-32%',
    gmv: '₹4,200 Cr',
    monthlyActiveUsers: '15M+',
    aovBand: '₹1,800-4,500 (Palette) | ₹3,500+ (Luxury)',
    categoryFocus: 'Luxury (CLiQ Luxury), Fashion + Home + Electronics (Palette)',
    sellerRequirements: 'GST, brand trademark, authenticity letters (Luxury), Tata Neu loyalty integration, ₹3,500+ AOV floor for Luxury',
    logistics: 'Tata Cliq Fulfilment + Self-ship, integration with Tata Neu ecosystem',
    returns: '15-day return window, RTO 12-18%',
    settlementCycle: 'T+15 days',
    marketing: 'Tata Neu (loyalty), Luxury Week, Diwali Luxe, Croma cross-promo',
    advertising: 'Sponsored Products, Brand Store, Tata Neu-targeted retargeting',
    catalogQuality: 'Very high (curated, Luxury track authenticity-checked)',
    brandSuit: 'Luxury designers, premium fashion, heritage brands, Tata Neu ecosystem',
    growthPotential: 'High',
    amComplexity: 'Medium',
    hubHref: '/tata-cliq-account-management',
    approvalHref: '/tata-cliq-account-management',
    pros: [
      'Tata Group brand equity + Neu-loyalty consumer',
      'Luxury track is India\'s most curated luxury lane',
      'Cross-category (fashion, home, electronics, beauty)',
      'Highest AOV band across mainstream marketplaces',
    ],
    cons: [
      'Lower GMV (₹4,200 Cr) vs Myntra/Amazon',
      'Approval 25-40 days (slowest in fashion)',
      '₹3,500+ AOV floor blocks mass-market brands from Luxury',
    ],
  },
  blinkit: {
    name: 'Blinkit',
    slug: 'blinkit',
    logo: 'BL',
    accent: 'from-yellow-400 to-amber-500',
    timeline: MARKETPLACES.blinkit.timeline,
    approvalDifficulty: MARKETPLACES.blinkit.approvalDifficulty,
    commission: '25-40%',
    gmv: '₹18,500 Cr',
    monthlyActiveUsers: '15M+',
    aovBand: MARKETPLACES.blinkit.aovBand,
    categoryFocus: MARKETPLACES.blinkit.categoryFocus,
    sellerRequirements: 'GST, FSSAI, dark-store-friendly packaging (<300g preferred), SKU velocity >5 units/store/day',
    logistics: 'Blinkit dark-stores + 15-min delivery, Zomato last-mile',
    returns: 'Same-day refund for damage / wrong item, low return rate',
    settlementCycle: 'T+7 days',
    marketing: 'Blinkit Sale, Zomato cross-promo, PLA + Sponsored Search',
    advertising: 'Sponsored Products, Sponsored Brands, Aisle Ads (dark-store shelf)',
    catalogQuality: 'Medium (velocity > polish)',
    brandSuit: 'Impulse-buy FMCG, beauty travel sizes, deodorants, snacks, baby essentials, D2C QC-native',
    growthPotential: MARKETPLACES.blinkit.growthPotential,
    amComplexity: MARKETPLACES.blinkit.amComplexity,
    hubHref: '/blinkit-account-management',
    approvalHref: '/blinkit-seller-registration',
    pros: [
      'India\'s #1 Quick Commerce (₹18,500 Cr GMV)',
      'Zomato-owned — cross-app 100M+ MAU discovery',
      '15-min delivery to 35+ cities',
      'Fast settlement (T+7)',
    ],
    cons: [
      'Commission 25-40% (highest in QC)',
      'Dark-store SKU cap forces aggressive velocity',
      'Packaging must be <300g for optimal picking',
    ],
  },
  zepto: {
    name: 'Zepto',
    slug: 'zepto',
    logo: 'ZP',
    accent: 'from-purple-500 to-fuchsia-500',
    timeline: MARKETPLACES.zepto.timeline,
    approvalDifficulty: MARKETPLACES.zepto.approvalDifficulty,
    commission: '25-38%',
    gmv: '₹14,200 Cr',
    monthlyActiveUsers: '12M+',
    aovBand: MARKETPLACES.zepto.aovBand,
    categoryFocus: MARKETPLACES.zepto.categoryFocus,
    sellerRequirements: 'GST, FSSAI, dark-store-friendly packaging, aggressive PLA appetite, tier-1 metro fit',
    logistics: 'Zepto dark-stores + 10-min delivery, tier-1 metro focus',
    returns: 'Instant refund for damage, low return rate',
    settlementCycle: 'T+7 days',
    marketing: 'Zepto Cafe, Zepto Pass, PLA + Sponsored Search',
    advertising: 'Sponsored Products, Sponsored Search, Aisle Ads, Zepto Ads (self-serve)',
    catalogQuality: 'Medium-High (aggressive curation for velocity)',
    brandSuit: 'Premium impulse-buy, D2C beauty travel sizes, snacks, F&B, wellness',
    growthPotential: MARKETPLACES.zepto.growthPotential,
    amComplexity: MARKETPLACES.zepto.amComplexity,
    hubHref: '/zepto-account-management',
    approvalHref: '/zepto-seller-registration',
    pros: [
      'Fastest-growing QC (200%+ YoY, ₹14,200 Cr GMV)',
      '10-min delivery — fastest in market',
      'Premium tier-1 metro shopper base',
      'Zepto Ads self-serve is best UI in QC',
    ],
    cons: [
      'Tier-1 metro-only (limited tier-2/3 reach)',
      'Commission 25-38% + aggressive PLA needed',
      'Dark-store cap forces high velocity',
    ],
  },
  instamart: {
    name: 'Swiggy Instamart',
    slug: 'swiggy-instamart',
    logo: 'SI',
    accent: 'from-orange-500 to-red-500',
    timeline: MARKETPLACES.instamart.timeline,
    approvalDifficulty: MARKETPLACES.instamart.approvalDifficulty,
    commission: '26-38%',
    gmv: '₹9,800 Cr',
    monthlyActiveUsers: '500M+ (via Swiggy)',
    aovBand: MARKETPLACES.instamart.aovBand,
    categoryFocus: MARKETPLACES.instamart.categoryFocus,
    sellerRequirements: 'GST, FSSAI, Swiggy ecosystem alignment (food + grocery), margin discipline',
    logistics: 'Swiggy Instamart dark-stores + 15-min delivery, Swiggy last-mile',
    returns: 'Instant refund for damage, low return rate',
    settlementCycle: 'T+10 days',
    marketing: 'Swiggy cross-app (food delivery), Instamart Days, PLA',
    advertising: 'Sponsored Products, Sponsored Brand, Category Ads',
    catalogQuality: 'Medium (velocity-first)',
    brandSuit: 'F&B, snacks, beverages, personal care, kitchen essentials, brands wanting Swiggy 500M+ MAU cross-app discovery',
    growthPotential: MARKETPLACES.instamart.growthPotential,
    amComplexity: MARKETPLACES.instamart.amComplexity,
    hubHref: '/swiggy-instamart-account-management',
    approvalHref: '/swiggy-instamart-seller-registration',
    pros: [
      'Cross-app discovery from 500M+ Swiggy food-delivery MAU',
      'F&B category dominance',
      '15-min delivery to 25+ cities',
      'Instamart Days sale events drive velocity',
    ],
    cons: [
      'Lower GMV (₹9,800 Cr) vs Blinkit/Zepto',
      'Commission 26-38% + Swiggy margin structure',
      'Sub-app inside Swiggy — less standalone recall',
    ],
  },
  amazon: {
    name: 'Amazon India',
    slug: 'amazon',
    logo: 'AZ',
    accent: 'from-slate-800 to-slate-900',
    timeline: '7-14 days',
    approvalDifficulty: 'Low',
    commission: '8-25%',
    gmv: '₹280,000 Cr',
    monthlyActiveUsers: '250M+',
    aovBand: '₹450-1,800',
    categoryFocus: 'Everything (Fashion, Beauty, F&B, Electronics, Home)',
    sellerRequirements: 'GST, PAN, brand registry (recommended), FBA-ready packaging',
    logistics: 'FBA (Fulfilment by Amazon) + Easy-Ship + Self-ship, largest logistics network in India',
    returns: '10-30 day return window, RTO 15-22%',
    settlementCycle: 'T+7 to T+14 days',
    marketing: 'Prime Day, Great Indian Festival, Amazon Live, Amazon Ads',
    advertising: 'Sponsored Products, Sponsored Brands, Sponsored Display, DSP, Amazon Ads',
    catalogQuality: 'Medium (self-serve, quality varies)',
    brandSuit: 'Volume-first brands, tier-2/3 reach, repeat-purchase mechanics, FBA-native',
    growthPotential: 'High',
    amComplexity: 'Medium',
    hubHref: '/amazon-seller-account-management',
    approvalHref: '/amazon-brand-registry-india',
    pros: [
      'Largest eCommerce marketplace in India (₹280,000 Cr GMV)',
      'FBA logistics + tier-2/3 reach unmatched',
      'Lowest commission (8-25%)',
      'Self-serve approval (7-14 days)',
    ],
    cons: [
      'Minimal editorial discovery — PLA-driven',
      'Brand-building harder vs curated marketplaces',
      'Fashion catalog polish is inconsistent',
    ],
  },
};

// ── Pair-specific narrative + comparison config ───────────────────────
const build = ({ aKey, bKey, ...rest }) => ({
  slug: `${MARKETPLACE_ATTRS[aKey].slug}-vs-${MARKETPLACE_ATTRS[bKey].slug}`,
  a: MARKETPLACE_ATTRS[aKey],
  b: MARKETPLACE_ATTRS[bKey],
  ...rest,
});

export const COMPARISONS = [
  build({
    aKey: 'myntra',
    bKey: 'ajio',
    winner: 'Myntra for volume + editorial curation; AJIO for emerging D2C + AJIO Luxe designers.',
    metaTitle: 'Myntra vs AJIO 2026 — Which Fashion Marketplace | Zaroori Retail',
    metaDescription: 'Myntra vs AJIO 2026 comparison: 10-15 days approval on Myntra vs 16-26 days on AJIO. Commission, GMV, category fit, and 3-track vs curated model compared.',
    directAnswer:
      'Myntra wins for mass-fashion reach (70M+ MAU, ₹35,000 Cr GMV, agency-filed approval in 10-15 days). AJIO wins for emerging D2C via its Indie track (16-26 days approval, easier entry) and luxury via AJIO Luxe. Most fashion brands launch on both in parallel — there is no cross-marketplace conflict.',
    whoShouldChooseA:
      'Established fashion brands with 20+ SKUs, professional studio photography budget, and appetite for editorial content velocity should start with Myntra. Best fit: bridge-premium D2C, footwear, accessories, and mainstream apparel targeting the 70M+ MAU shopper base.',
    whoShouldChooseB:
      'Emerging D2C brands, tier-2/3 mainstream, and AJIO Luxe designers should start with AJIO. Its 3-track structure (Brand Mall ₹399+ / Luxe ₹3,500+ / Indie ₹1,200+) fits every AOV band, and Reliance ecosystem cross-promo (JioMart, Trends) accelerates cold-start.',
  }),
  build({
    aKey: 'nykaa',
    bKey: 'tataCliq',
    winner: 'Nykaa for pure beauty leadership; Tata CLiQ for luxury cross-category.',
    metaTitle: 'Nykaa vs Tata CLiQ 2026 — Beauty vs Luxury Marketplace | Zaroori Retail',
    metaDescription: 'Nykaa vs Tata CLiQ 2026: 20-25 days approval on Nykaa vs 25-40 days on Tata CLiQ. Beauty category vs luxury cross-category comparison + FAQ.',
    directAnswer:
      'Nykaa is India\'s dedicated beauty leader (₹6,500 Cr GMV, 20-25 day approval, curated buyer team). Tata CLiQ splits into Luxury (₹3,500+ AOV designer + luxury beauty) and Palette (mainstream cross-category). Choose Nykaa if beauty is your core; Tata CLiQ if you are a luxury cross-category brand needing Tata Neu ecosystem exposure.',
    whoShouldChooseA:
      'Pure beauty, cosmetics, skincare, and wellness brands with FSSAI/CDSCO compliance and D2C traction proof should choose Nykaa. Best fit: content-led brand books, ingredient story, and appetite for Pink Pass discount funding.',
    whoShouldChooseB:
      'Luxury designer brands (fashion, home, beauty) with ₹3,500+ AOV, authenticity documentation, and interest in Tata Neu loyalty integration should choose Tata CLiQ. Also suitable for heritage cross-category brands wanting Croma + Reliance Trends cross-promo.',
  }),
  build({
    aKey: 'blinkit',
    bKey: 'zepto',
    winner: 'Blinkit for GMV + reach; Zepto for velocity + tier-1 premium.',
    metaTitle: 'Blinkit vs Zepto 2026 — Which Quick Commerce Platform | Zaroori Retail',
    metaDescription: 'Blinkit vs Zepto 2026 QC comparison: ₹18,500 Cr vs ₹14,200 Cr GMV, 15-min vs 10-min delivery, dark-store reach + ad tools compared.',
    directAnswer:
      'Blinkit leads on absolute GMV (₹18,500 Cr, Zomato-owned, 15-min delivery to 35+ cities). Zepto is the fastest-growing QC (₹14,200 Cr GMV, 200%+ YoY, 10-min delivery, premium tier-1 focus). Both accept new brands in 7-12 days. Most impulse-buy brands launch on both in parallel to maximize dark-store SKU velocity.',
    whoShouldChooseA:
      'FMCG, snacks, beverages, baby essentials, and mainstream personal-care brands wanting the largest QC reach should start with Blinkit. Best fit: dark-store-friendly packaging (<300g), high SKU velocity, and appetite for Zomato cross-app discovery.',
    whoShouldChooseB:
      'Premium D2C impulse-buy brands (beauty travel sizes, snacks, F&B, wellness) targeting tier-1 metro shoppers should start with Zepto. Best fit: aggressive PLA appetite, Zepto Cafe co-branding potential, and premium AOV positioning.',
  }),
  build({
    aKey: 'zepto',
    bKey: 'instamart',
    winner: 'Zepto for pure velocity; Instamart for cross-app discovery.',
    metaTitle: 'Zepto vs Swiggy Instamart 2026 — Quick Commerce Comparison | Zaroori Retail',
    metaDescription: 'Zepto vs Swiggy Instamart 2026: dedicated 10-min app vs cross-app QC inside Swiggy 500M+ MAU. Dark-store velocity + category fit compared.',
    directAnswer:
      'Zepto is a dedicated 10-min Quick Commerce app (₹14,200 Cr GMV) focused on tier-1 metros with premium positioning. Swiggy Instamart lives inside the Swiggy app (₹9,800 Cr GMV, 15-min delivery) with cross-app discovery from 500M+ Swiggy food-delivery MAU. Instamart wins for F&B; Zepto wins for pure impulse-buy velocity.',
    whoShouldChooseA:
      'Premium D2C brands, beauty travel sizes, wellness, and impulse-buy SKUs targeting tier-1 metro shoppers should start with Zepto. Best fit: fast settlement, aggressive PLA, Zepto Ads self-serve.',
    whoShouldChooseB:
      'F&B, snacks, beverages, and brands wanting the halo of Swiggy\'s 500M+ MAU food-delivery cross-app should choose Instamart. Best fit: kitchen essentials, RTE, RTC, breakfast SKUs.',
  }),
  build({
    aKey: 'tataCliq',
    bKey: 'ajio',
    winner: 'AJIO for emerging brands + scale; Tata CLiQ Luxury for premium positioning.',
    metaTitle: 'Tata CLiQ vs AJIO 2026 — Luxury vs Emerging Fashion | Zaroori Retail',
    metaDescription: 'Tata CLiQ vs AJIO 2026: Tata CLiQ Luxury vs AJIO Luxe track. Commission, approval, brand fit, and 3-track vs 2-track models compared.',
    directAnswer:
      'Tata CLiQ Luxury is India\'s most curated luxury lane (Tata Group equity, Neu-loyalty ecosystem, 25-40 day approval, ₹3,500+ AOV floor). AJIO\'s 3-track structure (Brand Mall / Luxe / Indie, 16-26 day approval) is more flexible for emerging D2C. Most luxury brands launch on both — Tata CLiQ Luxury for premium positioning, AJIO Luxe for scale.',
    whoShouldChooseA:
      'Luxury designers, heritage brands, and premium cross-category (fashion, home, beauty) with ₹3,500+ AOV, authenticity documentation, and appetite for Tata Neu integration should choose Tata CLiQ.',
    whoShouldChooseB:
      'Emerging D2C fashion brands, tier-2/3 mainstream, and AJIO Luxe designers should start with AJIO. Its 3-track structure fits AOV bands from ₹399 to ₹8,400+, and Reliance ecosystem accelerates cold-start.',
  }),
  build({
    aKey: 'myntra',
    bKey: 'tataCliq',
    winner: 'Myntra for volume + mainstream fashion; Tata CLiQ for luxury positioning.',
    metaTitle: 'Myntra vs Tata CLiQ 2026 — Fashion Marketplace Comparison | Zaroori Retail',
    metaDescription: 'Myntra vs Tata CLiQ 2026: ₹35,000 Cr mass fashion vs ₹4,200 Cr luxury cross-category. Commission, approval, brand fit compared.',
    directAnswer:
      'Myntra dominates mass fashion (₹35,000 Cr GMV, 70M+ MAU, 10-15 day approval) with MAM-tier growth mechanics. Tata CLiQ (₹4,200 Cr GMV) splits Luxury (₹3,500+ AOV designer + heritage) from Palette (mainstream + Neu-loyalty). Choose Myntra for volume + PLA-driven growth; Tata CLiQ for luxury designer positioning + Tata Group brand equity.',
    whoShouldChooseA:
      'Mainstream and bridge-premium fashion brands wanting the largest fashion audience (70M+ MAU) should start with Myntra. Best fit: 20+ SKUs, professional photography, MAM-tier growth appetite.',
    whoShouldChooseB:
      'Luxury designers, heritage brands, and premium cross-category (fashion, home, beauty) with ₹3,500+ AOV should choose Tata CLiQ. Best fit: brands wanting Tata Group equity and Neu-loyalty cross-promo.',
  }),
  build({
    aKey: 'nykaa',
    bKey: 'myntra',
    winner: 'Nykaa for pure beauty; Myntra for cross-category + fashion.',
    metaTitle: 'Nykaa vs Myntra 2026 — Beauty vs Fashion Marketplace | Zaroori Retail',
    metaDescription: 'Nykaa vs Myntra 2026: ₹6,500 Cr beauty leader vs ₹35,000 Cr fashion giant. Commission, approval, category fit compared.',
    directAnswer:
      'Nykaa is India\'s dedicated beauty leader (₹6,500 Cr, curated buyer team, 20-25 day approval, 28% repeat-purchase). Myntra offers scale (₹35,000 Cr fashion GMV, growing beauty at 40% YoY, 10-15 day approval) with cross-category discovery. Pure beauty brands should lead with Nykaa. Beauty-adjacent + fashion brands launch on both in parallel.',
    whoShouldChooseA:
      'Pure beauty, cosmetics, skincare, and wellness brands with FSSAI/CDSCO compliance should choose Nykaa. Best fit: ingredient story, content-led brand book, D2C/Instagram traction, and appetite for Pink Pass discount funding.',
    whoShouldChooseB:
      'Fashion brands with a beauty-adjacent line (fashion-beauty crossover D2C) and mass-market beauty targeting 70M+ MAU should choose Myntra. Best fit: bridge-premium D2C wanting MAM-tier growth mechanics.',
  }),
  build({
    aKey: 'amazon',
    bKey: 'myntra',
    winner: 'Amazon for volume + logistics; Myntra for fashion curation.',
    metaTitle: 'Amazon vs Myntra 2026 — Fashion Marketplace Comparison | Zaroori Retail',
    metaDescription: 'Amazon vs Myntra 2026 for fashion: 7-14 days Amazon approval vs 10-15 days Myntra. Commission, discovery model, brand fit compared.',
    directAnswer:
      'Amazon accepts new brands in 7-14 days with the lowest commission (8-25%) but minimal editorial discovery — PLA and reviews determine outcomes. Myntra is curated (10-15 day approval, Medium difficulty) with buyer-team gating but higher AOV + fashion-native audience (70M+ MAU). Fashion brands should lead with Myntra + AJIO; use Amazon for tier-2/3 volume + repeat-purchase mechanics.',
    whoShouldChooseA:
      'Volume-first brands, tier-2/3 reach, repeat-purchase mechanics, and FBA-native operators should choose Amazon. Best fit: mass-market apparel, essentials, accessories, and brands wanting the largest ad tool suite (SP, SB, SD, DSP).',
    whoShouldChooseB:
      'Bridge-premium D2C fashion, footwear, accessories, and brands wanting editorial curation + higher AOV (₹1,650-2,400) should choose Myntra. Best fit: 20+ SKUs, professional studio photography, MAM-tier growth appetite.',
  }),
];

// ── FAQ generator (used by V2 template for FAQPage schema + on-page FAQ) ─
export const buildFaqsFor = (cmp) => {
  const { a, b, winner, directAnswer } = cmp;
  return [
    {
      q: `Which is faster to onboard: ${a.name} or ${b.name}?`,
      a: `${a.name} typically approves brands in ${a.timeline}. ${b.name} typically approves in ${b.timeline}. Faster approval does not guarantee better outcomes — category fit and account-management readiness matter more than raw speed.`,
    },
    {
      q: `Which has lower commission: ${a.name} or ${b.name}?`,
      a: `${a.name} commission is ${a.commission}. ${b.name} commission is ${b.commission}. Effective commission depends on category, PLA spend, and Fulfilment (FBA vs Self-ship). Consult a marketplace advisor before assuming published rates.`,
    },
    {
      q: `Which has higher growth potential: ${a.name} or ${b.name}?`,
      a: `${a.name} has ${a.growthPotential} growth potential (₹${a.gmv} GMV). ${b.name} has ${b.growthPotential} growth potential (${b.gmv} GMV). Growth headroom depends on category penetration, not raw platform GMV.`,
    },
    {
      q: `Should I launch on both ${a.name} and ${b.name}?`,
      a: `Yes — most brands run parallel launches on both. ${winner} Running both in parallel prevents category-cannibalization risk and captures shopper cohorts unique to each marketplace.`,
    },
    {
      q: `${a.name} vs ${b.name} — final recommendation?`,
      a: directAnswer,
    },
  ];
};

// ── Lookups + internal-linking helpers ────────────────────────────────
export const COMPARISON_BY_SLUG = Object.fromEntries(COMPARISONS.map((c) => [c.slug, c]));

export const COMPARISON_PATHS = COMPARISONS.map((c) => `/${c.slug}`);

export const getComparisonsByMarketplace = (key) => {
  const target = key === 'instamart' ? 'swiggy-instamart' : key === 'tataCliq' ? 'tata-cliq' : key;
  return COMPARISONS.filter((c) => c.a.slug === target || c.b.slug === target);
};

export const getRelatedComparisons = (currentSlug, limit = 4) =>
  COMPARISONS.filter((c) => c.slug !== currentSlug).slice(0, limit);
